import { RowCrudStatus } from "../../common/types";
import {
  TenantLlmConfig,
  TenantLlmConfigCreateMutationVariables,
  TenantLlmConfigUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { llmProviderDisplayId } from "../../llmProvider/store/LlmProviderCommonStore";
import { blankToNull } from "../../../common/blankToNull";

export type TenantLlmConfigValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __provider: string;
  showProvider: { displayId: string; id: string } | null;
  model: string;
  apiKey: string;
  apiKeyHasValue: boolean;
  baseUrl: string;
  enabled: boolean;
  priority: number;
  maxTokens: number | undefined;
  temperature: number | undefined;
  debugFullMode: boolean;
};

export type TenantLlmConfigErrorsType = {
  provider?: { message: string; fieldLabel: string };
  model?: { message: string; fieldLabel: string };
  apiKey?: { message: string; fieldLabel: string };
  baseUrl?: { message: string; fieldLabel: string };
  enabled?: { message: string; fieldLabel: string };
  priority?: { message: string; fieldLabel: string };
  maxTokens?: { message: string; fieldLabel: string };
  temperature?: { message: string; fieldLabel: string };
  debugFullMode?: { message: string; fieldLabel: string };
};

export function TenantLlmConfigValuesInit(): TenantLlmConfigValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __provider: "",
    showProvider: null,
    model: "",
    apiKey: "",
    apiKeyHasValue: false,
    baseUrl: "",
    enabled: false,
    priority: 1,
    maxTokens: undefined,
    temperature: undefined,
    debugFullMode: false,
  };
}

export type TenantLlmConfigType = TenantLlmConfig | TenantLlmConfigValuesType;

export function tenantLlmConfigDisplayId(
  source: Partial<TenantLlmConfig>,
  _langStore: LangStore
) {
  return (
    (source.provider
      ? llmProviderDisplayId(source.provider, _langStore)
      : _langStore.emptyStringSymbol) +
    "-" +
    (source.model ?? _langStore.emptyStringSymbol)
  );
}

export function getTenantLlmConfigValues(
  source: TenantLlmConfigType,
  _langStore: LangStore,
  _duplicated: boolean = false
): TenantLlmConfigValuesType {
  const entityValues = TenantLlmConfigValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : tenantLlmConfigDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showProvider =
    "provider" in source && source.provider
      ? {
          id: source.provider.id,
          displayId: llmProviderDisplayId(source.provider, _langStore),
        }
      : "showProvider" in source && source.showProvider
      ? { id: source.showProvider.id, displayId: source.showProvider.displayId }
      : null;
  entityValues.__provider = entityValues.showProvider?.displayId || "";
  entityValues.model = source.model;
  entityValues.apiKey = "";
  entityValues.apiKeyHasValue = !!source.apiKeyHasValue;
  entityValues.baseUrl = source.baseUrl || "";
  entityValues.enabled = source.enabled || false;
  entityValues.priority = source.priority || 0;
  entityValues.maxTokens =
    (source.maxTokens as unknown) === "" ||
    source.maxTokens === undefined ||
    source.maxTokens === null
      ? undefined
      : source.maxTokens;
  entityValues.temperature =
    (source.temperature as unknown) === "" ||
    source.temperature === undefined ||
    source.temperature === null
      ? undefined
      : source.temperature;
  entityValues.debugFullMode = source.debugFullMode || false;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getTenantLlmConfigUpdateVariables(
  values: TenantLlmConfigValuesType
): TenantLlmConfigUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    providerId: values.showProvider?.id || "",
    model: values.model,
    apiKey: values.apiKey || undefined,
    baseUrl: blankToNull(values.baseUrl),
    enabled: values.enabled,
    priority: values.priority,
    maxTokens: values.maxTokens === undefined ? null : values.maxTokens,
    temperature: values.temperature === undefined ? null : values.temperature,
    debugFullMode: values.debugFullMode,
  };
}

export function getTenantLlmConfigCreateVariables(
  values: TenantLlmConfigValuesType
): TenantLlmConfigCreateMutationVariables {
  return {
    providerId: values.showProvider?.id || "",
    model: values.model,
    apiKey: values.apiKey || undefined,
    baseUrl: blankToNull(values.baseUrl),
    enabled: values.enabled,
    priority: values.priority,
    maxTokens: values.maxTokens === undefined ? null : values.maxTokens,
    temperature: values.temperature === undefined ? null : values.temperature,
    debugFullMode: values.debugFullMode,
  };
}
